import { ReactElement } from "react";
import { FormattedMessage, useIntl } from "@/i18n/i18n-client";
import { Field, Form } from "react-final-form";
import { PrimaryButton } from "@/components/Button";
import InputPhone, { PhoneInput } from "@/shared/globals/UiElements/InputPhone";
import LoginSwitcher from "../../components/Authentication/LoginSwitcher";
import {
  LoginFormProps,
  LoginFormValues,
  LoginMethod,
  TemplateElement,
} from "../../../types";
import Flex from "@/shared/globals/UiElements/Flex";
import InputWithIcon from "@/shared/globals/UiElements/InputWithIcon";
import { AddUser } from "@/assets/Icons/AddUser";
import { RequiredSpan } from "../../../black-and-white/elements/Contact/styled";
import { Link } from "@/i18n/i18n-navigation";

const LoginForm: TemplateElement<LoginFormProps> = ({
  activeTab,
  setActiveTab,
  onSubmit,
  isError,
  setIsError,
  focusOnError,
}) => {
  const intl = useIntl();
  return (
    <Form<LoginFormValues>
      onSubmit={onSubmit}
      validate={(values) => validate(values, activeTab)}
      decorators={[focusOnError]}
      render={({ handleSubmit, submitting }) => (
        <form onSubmit={handleSubmit}>
          <Flex column spacing="xl">
            <p className="m-0 text-2xl text-gray-900 font-semibold text-center">
              <FormattedMessage
                defaultMessage="Sign-in to your account"
                id="AD4Lep"
              />
            </p>
            <LoginSwitcher activeTab={activeTab} setActiveTab={setActiveTab} />
            <Flex column alignItems="start" fullWidth>
              {activeTab === LoginMethod.EMAIL && (
                <label className=" text-sm font-medium w-full">
                  <FormattedMessage defaultMessage="Email" id="sy+pv5" />
                  <Field name="email">
                    {({ input, meta: { error, touched } }) => (
                      <div className="mt-1.5">
                        <InputWithIcon
                          {...input}
                          type="email"
                          autoComplete="email"
                          className={
                            (error && touched) || isError ? "invalid" : ""
                          }
                          onChange={(event) => {
                            setIsError(false);
                            input.onChange(event);
                          }}
                          placeholder={intl.formatMessage({
                            defaultMessage: "Enter your email",
                            id: "5MDGuM",
                          })}
                        />
                        {error && touched && (
                          <RequiredSpan>{error}</RequiredSpan>
                        )}
                      </div>
                    )}
                  </Field>
                </label>
              )}
              {activeTab === LoginMethod.PHONE && (
                <label className=" text-sm font-medium w-full">
                  <FormattedMessage defaultMessage="Phone no." id="1SphRN" />
                  <Field<PhoneInput> name="phone">
                    {(fieldProps) => (
                      <InputPhone
                        {...fieldProps}
                        className={isError ? "invalid" : ""}
                        onChange={(event: PhoneInput) => {
                          setIsError(false);
                          fieldProps.input.onChange(event);
                        }}
                      />
                    )}
                  </Field>
                </label>
              )}
              <Flex column alignItems="flex-end" fullWidth>
                <label className="w-full  text-sm font-medium">
                  <FormattedMessage defaultMessage="Password" id="5sg7KC" />
                  <Field name="password">
                    {({ input, meta: { error, touched } }) => (
                      <div className="mt-1.5 w-full">
                        <InputWithIcon
                          {...input}
                          width="100%"
                          type="password"
                          autoComplete="current-password"
                          className={
                            (error && touched) || isError ? "invalid" : ""
                          }
                          onChange={(event) => {
                            setIsError(false);
                            input.onChange(event);
                          }}
                          placeholder="••••••••"
                        />
                        {error && touched && (
                          <RequiredSpan>{error}</RequiredSpan>
                        )}
                        {isError && (
                          <RequiredSpan>
                            <FormattedMessage
                              defaultMessage="Invalid credentials"
                              id="M77Nuo"
                            />
                          </RequiredSpan>
                        )}
                      </div>
                    )}
                  </Field>
                </label>
              </Flex>
              <PrimaryButton compact reversed className="!font-normal">
                <Link href="/reset-password">
                  <FormattedMessage
                    defaultMessage="Forgot password?"
                    id="V/JHlm"
                  />
                </Link>
              </PrimaryButton>
            </Flex>

            <PrimaryButton type="submit" fullWidth isLoading={submitting}>
              <FormattedMessage defaultMessage="Login" id="AyGauy" />
            </PrimaryButton>

            <Flex alignItems="center">
              <Flex
                width="30px"
                height="30px"
                className="bg-black/20 rounded-full"
                alignItems="center"
                justifyContent="center"
              >
                <AddUser />
              </Flex>
              <Flex column spacing="none">
                <span>
                  <FormattedMessage
                    defaultMessage="Don't Have an Account?"
                    id="kXiqHd"
                  />
                </span>
                <PrimaryButton
                  type="button"
                  style={{ justifyContent: "start" }}
                  compact
                  reversed
                >
                  <Link href="/registration">
                    <span className="font-bold">
                      <FormattedMessage
                        defaultMessage="Sign Up with E-mail"
                        id="4IIHHT"
                      />
                    </span>
                  </Link>
                </PrimaryButton>
              </Flex>
            </Flex>
          </Flex>
        </form>
      )}
    />
  );
};

export default LoginForm;

/**
 *
 * Functions
 *
 */

const validate = (values: LoginFormValues, activeTab: LoginMethod) => {
  const errors: Partial<{ [key in keyof LoginFormValues]: ReactElement }> = {};

  if (
    activeTab === LoginMethod.EMAIL &&
    (!values?.email?.trim() ||
      !values?.email?.match(
        /^([a-zA-Z0-9_.-])+@(([a-zA-Z0-9-])+\.)+([a-zA-Z0-9]{2,4})+$/
      ))
  ) {
    errors.email = (
      <FormattedMessage
        defaultMessage="Please enter a valid email address"
        id="oLpv29"
      />
    );
  }

  if (activeTab === LoginMethod.PHONE && !values?.phone?.isValid) {
    errors.phone = (
      <FormattedMessage
        defaultMessage="Please enter a valid phone number"
        id="FPcbu0"
      />
    );
  }

  if (!values?.password) {
    errors.password = (
      <FormattedMessage
        defaultMessage="Please enter your password"
        id="iiYrag"
      />
    );
  }

  return errors;
};
